import {isFeatureEnabled} from '@github-ui/feature-flags'
import ISSUE_VIEWER_VIEW_QUERY from '@github-ui/issue-viewer/IssueViewerViewQuery.graphql'
import {registerNavigatorApp} from '@github-ui/react-core/register-app'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {relayRoute} from '@github-ui/relay-route'
import {useEffect} from 'react'

import {App} from './App'
import CUSTOM_VIEWS_QUERY from './components/sidebar/__generated__/SavedViewsQuery.graphql'
import {
  IssueDashboardCustomViewPage,
  IssueDashboardKnownViewPage,
  IssueDashboardPage,
  IssueIndexPage,
  IssueRepoNewChoosePage,
  IssueRepoNewPage,
  IssueViewsPage,
  RepositoryMilestonePage,
  RepositoryProjectsPage,
  RepositorySavedViewPage,
  RepositoryViewNewPage,
} from './lazy-routes'
import CURRENT_VIEW_QUERY from './pages/__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import ISSUE_DASHBOARD_CUSTOM_VIEW_PAGE_QUERY from './pages/__generated__/IssueDashboardCustomViewPageQuery.graphql'
import ISSUE_DASHBOARD_KNOWN_VIEW_PAGE_QUERY from './pages/__generated__/IssueDashboardKnownViewPageQuery.graphql'
import ISSUE_DASHBOARD_INDEX_PAGE_QUERY from './pages/__generated__/IssueDashboardPageQuery.graphql'
import ISSUE_INDEX_PAGE_QUERY from './pages/__generated__/IssueIndexPageQuery.graphql'
import REPOSITORY_MILESTONE_PAGE_QUERY from './pages/__generated__/RepositoryMilestonePageQuery.graphql'
import {clientSideRelayDataGenerator, setRecordMap} from './pages/ClientSideRelayDataGenerator'
import URL_ARGUMENTS_METADATA_QUERY from './pages/issue-new/__generated__/InternalIssueNewPageUrlArgumentsMetadataQuery.graphql'
import ISSUE_NEW_CHOOSE_QUERY from './pages/issue-new/__generated__/IssueRepoNewChoosePageQuery.graphql'
import {IssueShowPage} from './pages/IssueShowPage'
import {VARIABLE_TRANSFORMERS} from './utils/variable-transformers'

const isDevelopment = () => process.env.NODE_ENV === 'development'

// When issues_new_react_data_router FF is on and the `/issues/new` Relay route is removed, `/:owner/:repo/issues/:number` still matches the path (treating "new" as `:number`).
// so we register a higher-prio static path (higher prio than a dynamic one) that forces a hard nav
// Forces a full page reload so the server serves the data-router app bundle.
function HardNavigate() {
  useEffect(() => {
    window.location.assign(window.location.href)
  }, [])
  return null
}

const ISSUES_INDEX_CACHE_TTL = 1000 * 60 * 60 * 24 // 24 hours in milliseconds

registerNavigatorApp('issues-react', () => {
  const relayEnvironment = relayEnvironmentWithMissingFieldHandlerForNode()
  setRecordMap(relayEnvironment)
  clientSideRelayDataGenerator({environment: relayEnvironment})

  const relayRouteConfig = {
    fallback: isDevelopment() ? 'Loading...' : '',
    relayEnvironment,
  }
  // The issues/new and issues/new/choose pages are migrating to the Data Router (paved path)
  // `issues` app. When `issues_new_react_data_router` is enabled, register placeholder routes that
  // force a hard navigation — without these, `/:owner/:repo/issues/:number` would match "new" as a
  // number param and render an empty issue viewer instead of handing off to the data-router app.
  const isIssueNewDataRouterEnabled = isFeatureEnabled('issues_new_react_data_router')
  return {
    App,
    routes: [
      ...(isIssueNewDataRouterEnabled
        ? [
            relayRoute({
              ...relayRouteConfig,
              path: '/:owner/:name/issues/new',
              resourceName: 'IssueRepoNew',
              title: 'New Issue',
              Component: HardNavigate,
              queryConfigs: {},
            }),
            relayRoute({
              ...relayRouteConfig,
              path: '/:owner/:name/issues/new/choose',
              resourceName: 'IssueRepoNewChoose',
              title: 'Choose an Issue Template',
              Component: HardNavigate,
              queryConfigs: {},
            }),
          ]
        : [
            relayRoute({
              ...relayRouteConfig,
              path: '/:owner/:name/issues/new',
              resourceName: 'IssueRepoNew',
              title: 'New Issue',
              Component: IssueRepoNewPage,
              transformVariables: VARIABLE_TRANSFORMERS['/:owner/:name/issues/new'],
              queryConfigs: {
                pageQuery: {
                  concreteRequest: URL_ARGUMENTS_METADATA_QUERY,
                  variableMappers: routeParams => {
                    return {
                      owner: routeParams.pathParams['owner'],
                      name: routeParams.pathParams['name'],
                    }
                  },
                },
              },
            }),
            relayRoute({
              ...relayRouteConfig,
              path: '/:owner/:name/issues/new/choose',
              resourceName: 'IssueRepoNewChoose',
              title: 'Choose an Issue Template',
              Component: IssueRepoNewChoosePage,
              transformVariables: VARIABLE_TRANSFORMERS['/:owner/:name/issues/new/choose'],
              queryConfigs: {
                pageQuery: {
                  concreteRequest: ISSUE_NEW_CHOOSE_QUERY,
                  variableMappers: routeParams => {
                    return {
                      owner: routeParams.pathParams['owner'],
                      name: routeParams.pathParams['name'],
                    }
                  },
                },
              },
            }),
          ]),
      relayRoute({
        ...relayRouteConfig,
        path: '/issues/assigned',
        resourceName: 'ClientSideView',
        title: 'Assigned to me',
        Component: IssueDashboardKnownViewPage,
        transitionType: 'transition-while-fetch',
        transformVariables: VARIABLE_TRANSFORMERS['/issues/assigned'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_DASHBOARD_KNOWN_VIEW_PAGE_QUERY,
          },
          customViewsQuery: {
            concreteRequest: CUSTOM_VIEWS_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/issues/mentioned',
        resourceName: 'ClientSideView',
        title: 'Mentioned',
        Component: IssueDashboardKnownViewPage,
        transitionType: 'transition-while-fetch',
        transformVariables: VARIABLE_TRANSFORMERS['/issues/mentioned'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_DASHBOARD_KNOWN_VIEW_PAGE_QUERY,
          },
          customViewsQuery: {
            concreteRequest: CUSTOM_VIEWS_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/issues/created',
        resourceName: 'ClientSideView',
        title: 'Created by me',
        Component: IssueDashboardKnownViewPage,
        transitionType: 'transition-while-fetch',
        transformVariables: VARIABLE_TRANSFORMERS['/issues/created'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_DASHBOARD_KNOWN_VIEW_PAGE_QUERY,
          },
          customViewsQuery: {
            concreteRequest: CUSTOM_VIEWS_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/issues/recent',
        resourceName: 'ClientSideView',
        title: 'Recent Activity',
        Component: IssueDashboardKnownViewPage,
        transitionType: 'transition-while-fetch',
        transformVariables: VARIABLE_TRANSFORMERS['/issues/recent'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_DASHBOARD_KNOWN_VIEW_PAGE_QUERY,
          },
          customViewsQuery: {
            concreteRequest: CUSTOM_VIEWS_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/views/new',
        resourceName: 'RepositoryViewNew',
        title: 'New view · :owner/:repo',
        Component: RepositoryViewNewPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/views/:id',
        resourceName: 'RepositorySavedView',
        title: 'View · :owner/:repo',
        Component: RepositorySavedViewPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/views',
        resourceName: 'IssueViews',
        title: 'Views · :owner/:repo',
        Component: IssueViewsPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/:number',
        resourceName: 'IssueShow',
        // The issue viewer manages its own document title (from the issue's title), so this route
        // intentionally provides an empty title to opt out of the generic title manager.
        title: '',
        Component: IssueShowPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/:number'],
        queryConfigs: {
          issueViewerViewQuery: {
            concreteRequest: ISSUE_VIEWER_VIEW_QUERY,
            variableMappers: routeParams => {
              return {
                owner: routeParams.pathParams['owner'],
                repo: routeParams.pathParams['repo'],
                number: routeParams.pathParams['number'] ? parseInt(routeParams.pathParams['number']) : undefined,
              }
            },
          },
        },
        maxAge: 10,
        cacheTTL: DEFAULT_RELAY_CACHE_TTL,
        exclude_from_cache_key: ['allowedOwner'],
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
        cacheTTL: ISSUES_INDEX_CACHE_TTL,
        maxAge: 10,
        exclude_from_cache_key: ['includeReactions'],
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/issues/:id',
        resourceName: 'View',
        title: '', // Custom view title is set dynamically
        Component: IssueDashboardCustomViewPage,
        transitionType: 'transition-while-fetch',
        transformVariables: VARIABLE_TRANSFORMERS['/issues/:id'],
        queryConfigs: {
          currentViewQuery: {
            concreteRequest: CURRENT_VIEW_QUERY,
            variableMappers: routeParams => {
              return {
                id: routeParams.pathParams['id'],
              }
            },
          },
          pageQuery: {
            concreteRequest: ISSUE_DASHBOARD_CUSTOM_VIEW_PAGE_QUERY,
          },
          customViewsQuery: {
            concreteRequest: CUSTOM_VIEWS_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/issues',
        resourceName: 'IssuesIndex',
        title: 'Issues',
        Component: IssueDashboardPage,
        transformVariables: VARIABLE_TRANSFORMERS['/issues'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_DASHBOARD_INDEX_PAGE_QUERY,
          },
          customViewsQuery: {
            concreteRequest: CUSTOM_VIEWS_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/created_by/:author',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/created_by/:author'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/created_by/app/:author',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/created_by/app/:author'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),

      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/assigned/:assignee',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/assigned/:assignee'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),

      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/milestone/:number',
        resourceName: 'RepoMilestoneShow',
        title: '', // Milestone-based title is set in the component; This should be empty otherwise NavigatorClientEntry.tsx undoes the dynamic title.
        // Document title will never be empty in practice since we have the Github fallback should the component not set it.
        Component: RepositoryMilestonePage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/milestone/:number'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: REPOSITORY_MILESTONE_PAGE_QUERY,
          },
        },
      }),
      // Milestone index, new, and edit routes are handled by the Data Router `issues` app.
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/projects',
        resourceName: 'RepoProjectsIndex',
        title: 'Projects · :owner/:repo',
        Component: RepositoryProjectsPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/projects/templates',
        resourceName: 'RepoProjectsIndex',
        title: 'Templates · :owner/:repo',
        Component: RepositoryProjectsPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/mentioned/:mentioned',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/mentioned/:mentioned'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/labels/:label',
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/labels/:label'],
        resourceName: 'IssueLabelIndex',
        title: 'Labels · :owner/:repo',
        Component: IssueIndexPage,
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),
    ],
  }
})
